import { getNationalHomepage } from '../../../helpers/get-national-homepage'
import { getGiftCards } from '../../../helpers/get-gift-cards'
import { getCentres } from '../../../helpers/get-centres'
import { getCentre } from '../../../helpers/get-centre'
import { Centre } from '../../../../types/centre'
import { ParentItem } from '../types'
import { LinkItem, Item } from '../../universal-header/types'
import { SharedApolloClientBase, TypedApolloClientBase } from '../../../types'

type GlobalStaticData = {
  giftCards: {
    giftCardNavLinks: LinkItem[]
    navigationMenu: Item[]
  }
  national: {
    nationalExperienceNavLinks: LinkItem[]
    navigationMenu: Item[]
  }
  centres?: Centre[]
}

type SharedStaticData = {
  giftCards: Item[]
  navigationMenu: Item[]
  centres: Centre[]
  primaryLinks: LinkItem[]
}

export type NationalStaticData = SharedStaticData & {
  tertiaryLinks: LinkItem[]
}

export type CentreStaticData = SharedStaticData & {
  centre: Centre & {
    navigationMenu: ParentItem[]
  }
  tertiaryLinks: LinkItem[]
}

export type GiftCardsStaticData = SharedStaticData & {
  tertiaryLinks?: LinkItem[]
}

export const fetchGlobalStaticData = async (
  client: SharedApolloClientBase,
  country: 'Australia' | 'New Zealand'
): Promise<GlobalStaticData> => {
  const typedClient = client as TypedApolloClientBase

  const [giftCards, national, centres] = await Promise.all([
    getGiftCards(typedClient, country),
    getNationalHomepage(typedClient, country),
    getCentres(typedClient, country),
  ])

  return {
    giftCards,
    national,
    centres,
  }
}

export const fetchNationalStaticData = async (
  client: SharedApolloClientBase,
  options: { country: 'Australia' | 'New Zealand' }
): Promise<NationalStaticData> => {
  const { country } = options

  const {
    giftCards: { navigationMenu: giftCardNavigationMenu, giftCardNavLinks },
    national: {
      navigationMenu: nationalExperienceNavigationMenu,
      nationalExperienceNavLinks,
    },
    centres,
  } = await fetchGlobalStaticData(client, country)

  return {
    giftCards: giftCardNavigationMenu ?? null,
    navigationMenu: nationalExperienceNavigationMenu ?? null,
    centres: centres ?? [],
    primaryLinks: nationalExperienceNavLinks || [],
    tertiaryLinks: [
      {
        label: 'Brands',
        url: '/shop/brands',
      },
      {
        label: 'The Edit',
        url: '/the-edit',
      },
      ...((giftCardNavLinks || []) as LinkItem[]),
    ],
  }
}

export const fetchCentreStaticData = async (
  client: SharedApolloClientBase,
  options: {
    country: 'Australia' | 'New Zealand'
    centre: string
  }
): Promise<CentreStaticData> => {
  const { country, centre: centreSlug } = options
  const typedClient = client as TypedApolloClientBase

  const [
    {
      giftCards: { navigationMenu: giftCardNavigationMenu, giftCardNavLinks },
      national: { navigationMenu: nationalExperienceNavigationMenu },
      centres,
    },
    { centre },
  ] = await Promise.all([
    fetchGlobalStaticData(client, country),
    getCentre(typedClient, { country, centre: centreSlug }),
  ])

  const primaryLinks: LinkItem[] = centre.navigationMenu.map(
    ({ title, items }) => ({
      label: title,
      url: '#',
      hamburgerMenuTarget: ['centre', title],
      simpleMenuItems: items,
    })
  )

  return {
    giftCards: giftCardNavigationMenu ?? null,
    navigationMenu: nationalExperienceNavigationMenu ?? null,
    centres: centres ?? [],
    centre: {
      ...centre,
      navigationMenu:
        centre.navigationMenu.map(item => ({ ...item, key: item?.title })) ||
        {},
    },
    primaryLinks,
    tertiaryLinks: [
      {
        label: 'Opening hours',
        url: `/${centre.slug}/opening-hours`,
      },
      ...(giftCardNavLinks || []),
    ],
  }
}

export const fetchGiftcardsStaticData = async (
  client: SharedApolloClientBase,
  options: { country: 'Australia' | 'New Zealand' }
): Promise<GiftCardsStaticData> => {
  const { country } = options

  const {
    giftCards: { navigationMenu: giftCardNavigationMenu },
    national: { navigationMenu: nationalExperienceNavigationMenu },
    centres,
  } = await fetchGlobalStaticData(client, country)

  const primaryLinks: LinkItem[] = giftCardNavigationMenu.map((item: Item) => {
    const linkItem = item as LinkItem
    const parentItem = item as ParentItem
    const link: LinkItem = {
      label: linkItem.label ?? parentItem.title,
      url: linkItem.url ?? '#',
    }
    if (parentItem.key) {
      link.hamburgerMenuTarget = ['gift-cards', parentItem.key]
    }
    if (parentItem.items) {
      link.simpleMenuItems = parentItem.items as LinkItem[]
    }
    return link
  })

  return {
    giftCards: giftCardNavigationMenu ?? null,
    navigationMenu: nationalExperienceNavigationMenu ?? null,
    centres: centres ?? [],
    primaryLinks,
    tertiaryLinks: [
      {
        label: 'Westfield Direct',
        url: '/',
      },
    ],
  }
}
